import { css } from '@emotion/react';
import { compareValues, setUnit, splitUnit, typeOf, unit } from '../_functions';
import { math } from 'polished';
import Mixin from '../../../util/mixin';

export function divide(dividend, divisor, precision = 10) {
  const [dividendValue, dividendUnit] = splitUnit(dividend);
  const [divisorValue, divisorUnit] = splitUnit(divisor);
  const quotient = Math.round((dividendValue / divisorValue) * precision) / precision;

  if (dividendUnit !== divisorUnit) {
    return setUnit(quotient, dividendUnit);
  } else {
    return setUnit(quotient, '');
  }
}

function setLocalVariables($){
  $ = Object.create($);
  $.rfsBaseFontSize = $.rfsBaseFontSize ?? "1.25rem";
  $.rfsFontSizeUnit = $.rfsFontSizeUnit ?? "rem";

  if ($.rfsFontSizeUnit != "rem" && $.rfsFontSizeUnit != "px") {
    throw new Error(`\`${$.rfsFontSizeUnit}\` is not a valid unit for rfs-font-size-unit. Use \`px\` or \`rem\`.`);
  }

  // Breakpoint at where font-size starts decreasing if screen width is smaller
  $.rfsBreakpoint = $.rfsBreakpoint ?? "1200px";
  $.rfsBreakpointUnit = $.rfsBreakpointUnit ?? "px";

  if ($.rfsBreakpointUnit != "px" && $.rfsBreakpointUnit != "em" && $.rfsBreakpointUnit != "rem") {
    throw new Error(`\`${$.rfsBreakpointUnit}\` is not a valid unit for rfs-breakpoint-unit. Use \`px\`, \`em\` or \`rem\`.`);
  }

  // Resize font size based on screen height and width
  $.rfsTwoDimensional = $.rfsTwoDimensional ?? "false";

  // Factor of decrease
  $.rfsFactor = $.rfsFactor ?? 10;

  if (typeOf($.rfsFactor) != "number" || $.rfsFactor <= 1) {
    throw new Error(`\`${$.rfsFactor}\` is not a valid rfs-factor, it must be greater than 1.`);
  }

  // Generate enable or disable classes. Possibilities: false, "enable" or "disable"
  $.rfsClass = $.rfsClass ?? "false";

  // 1 rem = $rfs-rem-value px
  $.rfsRemValue = $.rfsRemValue ?? 16;

  // Safari iframe resize bug: https://github.com/twbs/rfs/issues/14
  $.rfsSafariIframeResizeBugFix = $.rfsSafariIframeResizeBugFix ?? "false";

  // Disable RFS by setting $enable-responsive-font-sizes to false
  $.enableResponsiveFontSizes = $.enableResponsiveFontSizes ?? "true";

  // Cache $rfs-base-font-size unit
  $.rfsBaseFontSizeUnit = $.rfsBaseFontSizeUnit ?? unit($.rfsBaseFontSize);


  // Remove px-unit from $rfs-base-font-size for calculations
  if ($.rfsBaseFontSizeUnit === "px") {
    $.rfsBaseFontSize = divide($.rfsBaseFontSize, math(`${$.rfsBaseFontSize} * 0 + 1`));
  } else if ($.rfsBaseFontSizeUnit === "rem") {
    $.rfsBaseFontSize = divide(
      $.rfsBaseFontSize,
      divide(math(`${$.rfsBaseFontSize} * 0 + 1`), $.rfsRemValue)
    );
  }

  // Cache $rfs-breakpoint unit to prevent multiple calls
  $.rfsBreakpointUnitCache = unit($.rfsBreakpoint);

  // Remove unit from $rfs-breakpoint for calculations
  if ($.rfsBreakpointUnitCache === "px") {
    $.rfsBreakpoint = divide($.rfsBreakpoint, math(`${$.rfsBreakpoint} * 0 + 1`));
  } else if ($.rfsBreakpointUnitCache === "rem" || $.rfsBreakpointUnitCache === "em") {
    $.rfsBreakpoint = divide($rfs-breakpoint, divide($rfs-breakpoint * 0 + 1, $rfs-rem-value));
  }

  return $;
}


// Internal mixin that adds disable classes to the selector if needed.
export class RfsDisableClass extends Mixin {
  style({$, content}) {
    $ = setLocalVariables($);
    if ($.rfsClass === "disable") {
      return css`
        // Adding an extra class increases specificity, which prevents the media query to override the font size
        &,
        .disable-responsive-font-size &,
        &.disable-responsive-font-size {
          ${content};
        }
      `
    } else {
      return content;
    }
  }
}

// Internal mixin that adds enable classes to the selector if needed.
export class RfsEnableClass extends Mixin {
  style({$, content}) {
    $ = setLocalVariables($);
    if ($.rfsClass === "enable") {
      return css`
        .enable-responsive-font-size &,
        &.enable-responsive-font-size {
          ${content};
        }
      `
    } else {
      return content;
    }
  }
}

// Internal mixin used to determine which media query needs to be used
export class RfsMediaQuery extends Mixin {
  style({$, content}, [mqValue]) {
    $ = setLocalVariables($);
    if ($.rfsTwoDimensional) {
      return css`
        @media (max-width: ${mqValue}), (max-height: ${mqValue}) {
          ${content};
        }
      `
    } else {
      return css`
        @media (max-width: ${mqValue}) {
          ${content};
        }
      `
    }
  }
}

// The font-size & responsive-font-size mixins use RFS to rescale the font size
export class FontSize extends Mixin {
  style({$}, [fs, important=false]) {
    return new Rfs(fs, important).include($);
  }
}

export class ResponsiveFontSize extends Mixin {
  style({$}, [fs, important=false]) {
    return new Rfs(fs, important).include($);
  }
}

// Responsive font size mixin
export class Rfs extends Mixin {
  style({$}, [fs, important=false]) {
    $ = setLocalVariables($);
    const rules = [];
    // Cache $fs unit
    const fsUnit = typeOf(fs) === "number" ? unit(fs) : false;

    // Add !important suffix if needed
    const rfsSuffix = important ? " !important" : "";

    // If $fs isn't a number (like inherit) or $fs has a unit (not px or rem, like 1.5em) or $ is 0, just print the value
    if (!fsUnit || (fsUnit !== "" && fsUnit !== "px" && fsUnit !== "rem") || fs === 0) {
      rules.push(`font-size: ${fs}${rfsSuffix}`);
    } else {
      // Remove unit from $fs for calculations
      if (fsUnit === "px") {
        fs = divide(fs, fs * 0 + 1);
      } else if (fsUnit === "rem") {
        fs = divide(fs, divide(fs * 0 + 1, $.rfsRemValue));
      }

      // Set default font size
      const rfsStatic = $.rfsFontSizeUnit === "rem" ? `${divide(fs, $.rfsRemValue)}rem` : `${fs}px`;

      // Only add the media query if the font size is bigger than the minimum font size
      if (compareValues(fs, "<=", $.rfsBaseFontSize) || !$.enableResponsiveFontSizes) {
        rules.push(`font-size: ${rfsStatic}${rfsSuffix};`)
      } else {
        // Calculate the minimum font size for $fs
        const fsMin = $.rfsBaseFontSize + divide(math(`${fs} - ${$.rfsBaseFontSize}`), $.rfsFactor);

        // Calculate difference between $fs and the minimum font size
        const fsDiff = fs - fsMin;

        // Base font-size formatting
        const minWidth = $.rfsFontSizeUnit === "rem" ? `${divide(fsMin, $.rfsRemValue)}rem` : `${fsMin}px`;

        // Use `vmin` if two-dimensional is enabled
        const variableUnit = $.rfsTwoDimensional ? "vmin" : "vw";

        // Calculate the variable width between 0 and $rfs-breakpoint
        const variableWidth =  `${divide(math(`${fsDiff} * 100`, $.rfsBreakpoint))}${variableUnit}`;

        // Set the calculated font-size
        const rfsFluid = `calc(${minWidth} + ${variableWidth}) ${rfsSuffix}`;

        // Breakpoint formatting
        const mqValue = (
          $.rfsBreakpointUnit === "px" ?
          `${$.rfsBreakpoint}px` :
          `${divide($.rfsBreakpoint, $.rfsRemValue)}${$.rfsBreakpointUnit}`
        );

        rules.push(
          new RfsDisableClass().include($, css`
            font-size: ${rfsStatic}${rfsSuffix};
          `)
        )


        rules.push(
          new RfsMediaQuery(mqValue).include(
            $,
            css`
              ${RfsEnableClass().include(
                $,
                css`font-size: ${rfsFluid};`
              )}

              // Include safari iframe resize fix if needed
              min-width: ${$.rfsSafariIframeResizeBugFix ? math("0 * 1vw") : null};
            `
          )
        );
      }
    }

    return css(rules);
  }
}
